<template>
    <div class="CellForm">
        <div class="Form mb-2">
            <div class="Field mb-1">
                <WText
                    tag="label"
                    typo="label"
                    weight="semi"
                    :size="14"
                    uppercase
                    class="mb-1"
                >
                    {{ $t('imports.create.issues.form.currentValue.label') }}
                </WText>
                <WText
                    tag="p"
                    typo="p"
                    :size="16"
                >
                    "{{ currentValue }}"
                </WText>
            </div>

            <Tabs
                v-if="tabs.length > 1"
                v-model="activeTab"
                :tabs="tabs"
                class="mb-1"
            />

            <FadeTransition mode="out-in">
                <div
                    v-if="activeTab === tabs[0].key"
                    :key="activeTab"
                    class="Field mb-1"
                >
                    <WText
                        tag="label"
                        typo="label"
                        weight="semi"
                        :size="14"
                        uppercase
                        class="mb-1"
                    >
                        {{ $t('imports.create.issues.form.value.label') }}
                    </WText>

                    <div class="FormCol">
                        <div class="Input">
                            <template v-if="isSelect">
                                <WSelect
                                    :value="inputValue"
                                    name="value"
                                    :options="options"
                                    :placeholder="$t('actions.search')"
                                    :disabled="loading"
                                    multiple
                                    @input="handleInput"
                                />
                            </template>
                            <template v-else-if="fieldInputs[type] === inputs.BOOL">
                                <WSwitch
                                    :value="inputValue"
                                    name="value"
                                    :disabled="loading"
                                    @input="handleInput"
                                />
                            </template>
                            <template v-else-if="fieldInputs[type] === inputs.DATE">
                                <WDatepicker
                                    :value="inputValue"
                                    :placeholder="datePlaceholder"
                                    :formats="dateFormats"
                                    name="value"
                                    :disabled="loading"
                                    @input="handleInput"
                                />
                            </template>
                            <template v-else>
                                <WInput
                                    :value="inputValue"
                                    :placeholder="$t('imports.create.issues.form.value.placeholder')"
                                    name="value"
                                    type="text"
                                    :disabled="loading"
                                    @input="handleInput"
                                />
                            </template>
                        </div>
                    </div>
                </div>
                <div
                    v-else
                    :key="activeTab"
                >
                    <Sources
                        :value="mappers"
                        :isLoading="loading"
                        class="mb-2"
                        @input="handleMappers"
                    />
                </div>
            </FadeTransition>
        </div>
    </div>
</template>
<script>
import VueTypes from 'vue-types'
import moment from 'moment'
import { INPUTS, FIELD_INPUTS } from '@/entities/fields'
import { Tabs, Sources } from '@/components'
import {
    DATE_PICKER_FORMATS, DATE_PLACEHOLDER, toPickerDate, toApiDate,
} from './dates'

// TODO: Enable transforms after create services in back-end
const ENABLE_NORMALIZATION = false

export default {
    name: 'CellForm',
    components: {
        Tabs,
        Sources,
    },
    props: {
        value: VueTypes.any,
        rowId: VueTypes.oneOfType([VueTypes.string, VueTypes.number]),
        field: VueTypes.oneOfType([VueTypes.string, VueTypes.number]),
        type: VueTypes.string,
        options: VueTypes.arrayOf(VueTypes.shape({
            key: VueTypes.oneOfType([VueTypes.string, VueTypes.number]).isRequired,
            label: VueTypes.oneOfType([VueTypes.string, VueTypes.number]).isRequired,
        }).loose),
        formatDate: VueTypes.string.def('DD/MM/YYYY'),
        loading: VueTypes.bool,
    },
    data () {
        const tabs = [
            {
                key: 'edit',
                text: this.$t('imports.create.issues.edit'),
            },
            ...(ENABLE_NORMALIZATION ? [{
                key: 'normalization',
                text: this.$t('imports.create.issues.normalization'),
            }] : []),
        ]

        return {
            inputValue: '',
            currentValue: '',
            mappers: {},

            tabs,
            activeTab: tabs[0].key,
        }
    },
    computed: {
        isSelect () {
            return !!(FIELD_INPUTS[this.type] === INPUTS.SELECT && (this.options || []).length)
        },
        isDate () {
            return FIELD_INPUTS[this.type] === INPUTS.DATE
        },
    },
    beforeCreate () {
        this.inputs = INPUTS
        this.fieldInputs = FIELD_INPUTS
        this.dateFormats = DATE_PICKER_FORMATS
        this.datePlaceholder = DATE_PLACEHOLDER
    },
    mounted () {
        this.currentValue = this.normalizeCurrentValue({ type: this.type, value: this.value })
        this.inputValue = this.normalizeInputValue({ type: this.type, value: this.value })
    },
    methods: {
        handleInput (value) {
            this.inputValue = value
            this.notifyInput()
        },
        handleMappers (value) {
            this.mappers = value
            this.notifyInput()
        },
        notifyInput () {
            this.$emit('input', {
                _inputRowId: this.rowId,
                [this.field]: this.isDate ? toApiDate(this.inputValue) : this.inputValue,
                ...(ENABLE_NORMALIZATION ? { mappers: this.mappers } : {}),
            })
        },
        normalizeInputValue ({ type, value }) {
            if (FIELD_INPUTS[type] === INPUTS.DATE) return toPickerDate(value)
            return value
        },
        normalizeCurrentValue ({ type, value }) {
            if (!value) return value
            if (FIELD_INPUTS[type] === INPUTS.SELECT || Array.isArray(value)) return value.join(', ')
            if (FIELD_INPUTS[type] === INPUTS.DATE) return moment(value).format(this.formatDate)
            return value
        },
    },
}
</script>
<style lang="scss" scoped>
.WInput, .WSelect {
    width: 100%;
}

.Input {
    width: 50%;
}

.WDatepicker::v-deep .Input {
    margin-bottom: 0 !important;
}

.Option {
    & + .Option {
        margin-top: 10px;
    }

    &.Separator {
        &:before {
            content: '';
            display: block;
            margin-top: 10px;
            padding-top: 10px;
            border-top: 1px solid $gray-05;
            width: 100%;
        }
    }
}
</style>
